import React, { useState, useRef, useEffect } from "react";
import { Authenticator, ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import MatchSetup from "./MatchSetup";
import PressToTalk from "./PressToTalk";
import WelcomePage from "./WelcomePage";
import SessionPlan from "./SessionPlan";
import PricingTable from "./PricingTable";
import ManageTeamPage from "./ManageTeamPage";
import Navigation from "./Navigation";
import SetupCoachPage from "./SetupCoachPage";
import GamesStatsPage from "./GamesStatsPage";
import HowToUseApp from "./HowToUseApp";
import ContactUs from "./ContactUs";
// import CoachAssistant from "./components/CoachAssistant";
import TermsReview from "./TermsReview";
import ManageSubscription from "./ManageSubscription"; // Adjust the path if necessary
import StatsPage from "./StatsPage"; // Import StatsPage component
import "./css/App.css";
import { Amplify } from "aws-amplify"; // Keep Amplify for configuration
import { signOut } from "@aws-amplify/auth"; // Import signOut directly
import awsconfig from "./aws-exports";
import MatchLengthAndFormation from "./MatchLengthAndFormation"; // Import the new component

Amplify.configure(awsconfig);

const App = () => {
  const [matchFolderName, setMatchFolderName] = useState(null);
  const [players, setPlayers] = useState([]);
  const [myTeam, setMyTeam] = useState("");
  const [opposition, setOpposition] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [selectedTeamId, setSelectedTeamId] = useState(""); // Define selectedTeamId
  const [matchInfo, setMatchInfo] = useState({}); // or an appropriate initial value
  const [homeAway, setHomeAway] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [menuRef]);

  const theme = {
    name: "custom-theme",
    tokens: {
      colors: {
        brand: {
          primary: {
            100: "#ff7f50",
            80: "#ff6347",
            60: "#ff4500",
          },
        },
      },
    },
  };
  const customTheme = {
    name: "custom-auth-theme",
    tokens: {
      components: {
        authenticator: {
          container: {
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
            padding: "20px",
            backgroundColor: "white",
            borderRadius: "15px",
            boxShadow: "0 6px 20px rgba(0, 0, 0, 0.2)",
          },
          form: {
            button: {
              backgroundColor: "#0044cc",
              borderRadius: "8px",
              color: "#fff",
              hover: {
                backgroundColor: "#003399",
              },
            },
            input: {
              borderColor: "#ddd",
              borderRadius: "8px",
              padding: "10px",
              "@media (max-width: 480px)": {
                padding: "8px",
                fontSize: "0.9rem",
              },
            },
          },
        },
      },
    },
  };

  const handleMatchSetupComplete = ({
    matchFolderName,
    players,
    myTeam,
    opposition,
    userId,
    selectedTeamId,
    matchId,
    matchFolder,
    matchDate,
    status,
    otherInfo,
    homeAway,
    teamScores,
  }) => {
    setMatchFolderName(matchFolderName);
    setPlayers(players);
    setMyTeam(myTeam);
    setOpposition(opposition);
    setSelectedTeamId(selectedTeamId);
    setMatchInfo({
      matchId,
      match_folder: matchFolder,
      match_date: matchDate,
      status,
      otherInfo,
      teamScores, // Add teamScores here to ensure it's passed down
    });
    setHomeAway(homeAway);
  };

  // Custom sign-out function using the directly imported signOut function
  const handleSignOut = async () => {
    try {
      await signOut(); // Use signOut from @aws-amplify/auth
      window.location.reload(); // Reload the page to reflect the signed-out state
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Router>
        <div className="app-container">
          {/* Main Content */}
          <main className="app-main">
            <Authenticator
              // hideSignUp // Add this line if you want to hide the sign-up link
              components={{
                Header() {
                  return <div className="auth-logo"></div>;
                },
                Footer() {
                  return (
                    <p style={{ textAlign: "center", marginTop: "10px" }}>
                      © {new Date().getFullYear()} KICKS App. All rights
                      reserved.
                    </p>
                  ); // Remove Amplify footer if needed
                },
              }}
            >
              {({ user: authenticatedUser }) => {
                // If not authenticated, show nothing because the outer Authenticator handles login
                if (!authenticatedUser) {
                  return null; // Remove this inner Authenticator
                }

                // Extract user info when authenticated
                const userEmail =
                  authenticatedUser?.attributes?.email || "User";
                const userId = authenticatedUser?.username;

                return (
                  <div>
                    {/* Navigation should be available only after login */}
                    <Navigation
                      isMenuOpen={isMenuOpen}
                      setIsMenuOpen={setIsMenuOpen}
                      menuRef={menuRef}
                      signOut={handleSignOut} // Pass the manual sign-out function to Navigation
                      userId={userId} // Pass the userId explicitly
                    />

                    <Routes>
                      <Route
                        path="/"
                        element={
                          <WelcomePage
                            onSetupComplete={handleMatchSetupComplete} // Pass onSetupComplete
                            firstName={userEmail}
                          />
                        }
                      />
                      <Route
                        path="/terms-and-conditions"
                        element={<TermsReview userId={userId} />}
                      />
                      <Route path="/how-to-use" element={<HowToUseApp />} />

                      <Route
                        path="/match-setup"
                        element={
                          <MatchSetup
                            onSetupComplete={handleMatchSetupComplete}
                            userId={userId}
                          />
                        }
                      />
                      <Route
                        path="/match-length-formation"
                        element={<MatchLengthAndFormation />}
                      />
                      {/* <Route
                        path="/coach-assistant"
                        element={<CoachAssistant />}
                      /> */}

                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route
                        path="/press-to-talk"
                        element={
                          <PressToTalk
                            matchFolderName={matchFolderName}
                            players={players}
                            myTeam={myTeam}
                            opposition={opposition}
                            userId={userId}
                            selectedTeamId={selectedTeamId}
                            matchId={matchInfo.matchId} // Use matchId consistently
                            matchFolder={matchInfo.match_folder}
                            matchDate={matchInfo.match_date}
                            status={matchInfo.status}
                            otherInfo={matchInfo.otherInfo}
                            homeAway={homeAway}
                            teamScores={matchInfo.teamScores} // Ensure teamScores is passed
                          />
                        }
                      />
                      <Route
                        path="/SetupCoachPage"
                        element={<SetupCoachPage userId={userId} />}
                      />
                      <Route path="/plan-session" element={<SessionPlan />} />
                      <Route
                        path="/pricing"
                        element={<PricingTable userId={userId} />}
                      />
                      <Route
                        path="/manage-subscription"
                        element={<ManageSubscription userId={userId} />} // Pass userId as a prop
                      />

                      <Route
                        path="/games-stats"
                        element={<GamesStatsPage userId={userId} />}
                      />

                      <Route
                        path="/manage-team"
                        element={<ManageTeamPage userId={userId} />}
                      />
                      <Route
                        path="/stats"
                        element={<StatsPage coach_id={userId} />} // Pass userId as coach_id
                      />
                    </Routes>
                  </div>
                );
              }}
            </Authenticator>
          </main>

          {/* Footer */}
        </div>
      </Router>
    </ThemeProvider>
  );
};

export default App;
